/**
 * Form Field Components - The Intelligent Standard
 *
 * Refined form inputs with consistent styling and
 * proper accessibility.
 */

import {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

// Base label component
export interface FormLabelProps {
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
}

export function FormLabel({ htmlFor, required, children }: FormLabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-body-sm font-medium text-ink mb-2"
    >
      {children}
      {required && <span className="text-meridian ml-0.5">*</span>}
    </label>
  );
}

// Helper text
export function FormHelperText({
  children,
  error,
}: {
  children: ReactNode;
  error?: boolean;
}) {
  return (
    <p className={`text-caption mt-1.5 ${error ? 'text-error' : 'text-slate'}`}>
      {children}
    </p>
  );
}

// Input field
export interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: string;
}

export function InputField({
  label,
  helperText,
  error,
  required,
  className = '',
  id,
  ...props
}: InputFieldProps) {
  const inputId = id || props.name;

  return (
    <div className={className}>
      {label && (
        <FormLabel htmlFor={inputId} required={required}>
          {label}
        </FormLabel>
      )}
      <input
        id={inputId}
        className={`
          w-full px-4 py-3
          text-body text-ink font-body
          bg-white border rounded-lg
          transition-all duration-200
          placeholder:text-slate-light
          focus:outline-none focus:ring-2 focus:ring-meridian/10
          ${
            error
              ? 'border-error focus:border-error'
              : 'border-cream-dark focus:border-meridian'
          }
        `}
        required={required}
        {...props}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
      {helperText && !error && <FormHelperText>{helperText}</FormHelperText>}
    </div>
  );
}

// Textarea field
export interface TextareaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helperText?: string;
  error?: string;
}

export function TextareaField({
  label,
  helperText,
  error,
  required,
  className = '',
  id,
  ...props
}: TextareaFieldProps) {
  const textareaId = id || props.name;

  return (
    <div className={className}>
      {label && (
        <FormLabel htmlFor={textareaId} required={required}>
          {label}
        </FormLabel>
      )}
      <textarea
        id={textareaId}
        className={`
          w-full px-4 py-3
          text-body text-ink font-body
          bg-white border rounded-lg
          resize-y min-h-[100px]
          transition-all duration-200
          placeholder:text-slate-light
          focus:outline-none focus:ring-2 focus:ring-meridian/10
          ${
            error
              ? 'border-error focus:border-error'
              : 'border-cream-dark focus:border-meridian'
          }
        `}
        required={required}
        {...props}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
      {helperText && !error && <FormHelperText>{helperText}</FormHelperText>}
    </div>
  );
}

// Select field
export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  helperText?: string;
  error?: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

export function SelectField({
  label,
  helperText,
  error,
  required,
  options,
  placeholder,
  className = '',
  id,
  ...props
}: SelectFieldProps) {
  const selectId = id || props.name;

  return (
    <div className={className}>
      {label && (
        <FormLabel htmlFor={selectId} required={required}>
          {label}
        </FormLabel>
      )}
      <select
        id={selectId}
        className={`
          w-full px-4 py-3
          text-body text-ink font-body
          bg-white border rounded-lg
          transition-all duration-200
          cursor-pointer
          focus:outline-none focus:ring-2 focus:ring-meridian/10
          ${
            error
              ? 'border-error focus:border-error'
              : 'border-cream-dark focus:border-meridian'
          }
        `}
        required={required}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && <FormHelperText error>{error}</FormHelperText>}
      {helperText && !error && <FormHelperText>{helperText}</FormHelperText>}
    </div>
  );
}

// Checkbox field
export interface CheckboxFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  label: string;
  helperText?: string;
}

export function CheckboxField({
  label,
  helperText,
  className = '',
  id,
  ...props
}: CheckboxFieldProps) {
  const checkboxId = id || props.name;

  return (
    <div className={className}>
      <label
        htmlFor={checkboxId}
        className="flex items-start gap-3 cursor-pointer group"
      >
        <input
          type="checkbox"
          id={checkboxId}
          className="
            mt-0.5 w-5 h-5
            rounded border-cream-dark
            text-meridian
            focus:ring-meridian focus:ring-offset-0
            cursor-pointer
          "
          {...props}
        />
        <span className="text-body text-ink group-hover:text-meridian transition-colors">
          {label}
        </span>
      </label>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </div>
  );
}
